/* eslint-disable lit-a11y/click-events-have-key-events */
import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/**
 * Offer Card component for Edison.
 *
 * title - The title of the card.
 * subtitle - The subtitle of the card (optional).
 * inputName - The name of the form input (optional).
 * iconUrl - The URL to icon to display beside the title (optional).
 * iconSize - The icon width size in pixel.
 * imageUrl - The URL of the image.
 * imageSize - The image width size in pixel.
 * price - The price to display (optional).
 * ttcPrice - The ttc price in case PRO offer ?
 * promoLabel - The promo label under the price (optional).
 * frequency - The text next to the price (optional).
 * isPromo - Display a "Promo" badge instead of price (optional).
 * isFree - Display a "Offert" badge instead of price (optional).
 * light - Turn on the light-mode (white background) (default true).
 * disabled - Disabled or not (boolean).
 * actionName - The action button text (optional)
 * actionType - The action button type (optional)
 * title-icon - The button right to the title (optional).
 * button-card-content - The content inside the button card.
 * info - The content above action button.
 * expand - The content below action button. (slot)
 */
@customElement('nn-offer-card')
export class OfferCard extends LitElement {
  static styles = css`
    .button-card {
      align-items: flex-start;
      display: flex;
      font-family: 'font-medium';
      background: var(--nn-offer-card_button-card_background, #f7f8fb);
      border: 1px solid var(--nn-offer-card_button-card_border-color, #e8e8e8);
      border-radius: var(--nn-offer-card_button-card_border-radius, 16px);
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: start;
      padding: var(--nn-offer-card_button-card_padding, 16px);
      text-align: left;
    }
    .button-card__wrapper {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      width: 100%;
    }
    .button-card__wrapper--right {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      flex: 1;
      width: 100%;
    }
    .button-card--light {
      background: var(--nn-offer-card_button-card--light_background, #fff);
    }
    .button-card__container--left {
      display: unset;
      flex-wrap: nowrap;
      flex-direction: row;
      flex-basis: var(
        --nn-offer-card_button-card__container--left_flex-basis,
        65%
      );
      justify-content: space-between;
      margin: auto;
      text-align: center;
    }
    .button-card__container--right {
      align-items: flex-end;
      display: flex;
      flex-basis: var(
        --nn-offer-card_button-card__container--right_flex-basis,
        100%
      );
      flex-direction: row;
      flex-grow: 1;
      order: 3;
      text-align: center;
    }
    .button-card__container--second-price {
      display: flex;
      flex-direction: row;
      flex-grow: 1;
      flex-basis: 100%;
      font-size: var(
        --nn-offer-card_button-card__container--second-price_font-size,
        12px
      );
      justify-content: center;
      line-height: 14px;
      order: 4;
    }
    .button-card__container--bottom {
      flex-basis: 100%;
      max-width: 100%;
      order: 2;
      padding-bottom: 0;
    }
    .button-card__title_group {
      display: flex;
      flex-grow: 2;
      flex-direction: column;
      margin-top: auto;
      margin-bottom: 4px;
    }
    .button-card__title {
      font-family: font-medium;
      font-size: var(--nn-offer-card_button-card__title_font-size, 18px);
      font-weight: bold;
      margin-bottom: auto;
      margin-top: auto;
    }
    .button-card__subtitle {
      color: var(--nn-offer-card_button-card__subtitle_color, #494949);
      font-family: 'font-light';
      font-size: var(--nn-offer-card_button-card__subtitle_font-size, 14px);
      line-height: 10px;
    }
    .button-card__icon {
      margin-bottom: auto;
      padding: 0;
    }
    .button-card--disabled {
      background: rgba(0, 0, 0, 0);
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      cursor: not-allowed !important;
      opacity: 0.5;
    }
    .button-card:hover {
      border: 1px solid
        var(--nn-offer-card_button-card--hover_border-color, #598be7);
    }
    .button-card--disabled:hover {
      border: 1px solid
        var(--nn-offer-card_button-card--disabled--hover_border-color, #e8e8e8);
    }
    .button-card--1 {
      box-shadow: 0 9px 10px rgba(54, 54, 54, 0.09);
      cursor: pointer;
    }
    .button-card--5 {
      box-shadow: 0 9px 10px rgba(54, 54, 54, 0.09);
    }
    .button-card--2 {
      border: 2px solid
        var(--nn-offer-card_button-card--2_border-color, #598be7);
      cursor: pointer;
      padding: var(--nn-offer-card_button-card--2_padding, 16px);
    }
    .button-card--2:hover {
      border: 2px solid
        var(--nn-offer-card_button-card--2--hover_border-color, #598be7);
      padding: var(--nn-offer-card_button-card--2--hover_padding, 16px);
    }
    .button-card--3 .button-card__title,
    .button-card--4 .button-card__title,
    .button-card--6 .button-card__title {
      font-size: var(
        --nn-offer-card_button-card--all_button-card__title_font-size,
        14px
      );
      font-weight: bold;
    }
    .button-card--3 .button-card__price,
    .button-card--4 .button-card__price,
    .button-card--6 .button-card__price {
      font-size: var(
        --nn-offer-card_button-card--all_button-card__price_font-size,
        14px
      );
    }
    .button-card--3:hover,
    .button-card--4:hover {
      border: 1px solid
        var(--nn-offer-card_button-card--3-4--hover_border-color, #e8e8e8);
    }
    .button-card--6,
    .button-card--6:hover {
      border: 2px solid
        var(--nn-offer-card_button-card--6--hover_border-color, #598be7);
    }
    .button-card--3 .button-card__price .button-card__frequency,
    .button-card--4 .button-card__price .button-card__frequency,
    .button-card--6 .button-card__price .button-card__frequency {
      font-weight: normal;
    }
    .button-card__frequency {
      font-weight: bold !important;
    }
    .button-card__content {
      font-family: 'font-light';
      font-size: var(--nn-offer-card_button-card__content_font-size, 14px);
    }
    button {
      border-radius: var(--nn-offer-card_button_border-radius, 32px);
      border: 1px solid var(--nn-offer-card_button_border-color, #000);
      cursor: pointer;
      font-family: 'font-medium';
      font-size: var(--nn-offer-card_button_font-size, 14px);
      height: var(--nn-offer-card_button_height, 32px);
      padding: var(--nn-offer-card_button_padding, 6px 32px);
      min-width: var(--nn-offer-card_button_min-width, 110px);
      transition: var(--nn-offer-card_button_transition, 0.3s);
    }
    .button-card__action--1 {
      background: var(--nn-offer-card_button-card__action--1_background, #000);
      color: var(--nn-offer-card_button-card__action--1_color, #fff);
    }
    .button-card__action--2 {
      background: var(--nn-offer-card_button-card__action--2_background, #fff);
      color: var(--nn-offer-card_button-card__action--2_color, #000);
      border: 2px solid;
    }
    .button-card__action--3,
    .button-card__action--4,
    .button-card__action--6 {
      background: unset;
      border: none;
      color: var(--nn-offer-card_button-card__action--all_color, #000);
      font-size: var(--nn-offer-card_button-card__action--all_font-size, 12px);
      font-weight: bold;
      height: unset;
      text-decoration: underline;
      width: unset;
    }
    .button-card__action--1:hover {
      background: var(
        --nn-offer-card_button-card__action--1--hover_background,
        #fff
      );
      color: var(--nn-offer-card_button-card__action--1--hover_color, #000);
    }
    .button-card__action--2:hover,
    .button-card--disabled .button-card__action--1:hover {
      background: var(
        --nn-offer-card_button-card__action--2--hover_background,
        #000
      );
      color: var(--nn-offer-card_button-card__action--2--hover_color, #fff);
    }
    .button-card--disabled button {
      cursor: not-allowed;
    }
    .button-card__price {
      flex: 3;
      font-family: 'font-medium';
      font-size: var(--nn-offer-card_button-card__price_font-size, 14px);
      font-weight: bold;
      margin: var(--nn-offer-card_button-card__price_margin, 0);
    }
    .button-card__price--promo {
      flex: 2;
      font-family: 'font-light';
      font-size: var(--nn-offer-card_button-card__price--promo_font-size, 11px);
      margin-bottom: 0;
      margin-top: 2px;
      text-align: right;
    }
    .button-card__container--action {
      flex-basis: 100%;
      order: 5;
      text-align: center;
    }
    .button-card__warning {
      color: var(--nn-offer-card_button-card__warning_color, #c0392b);
      flex-basis: 100%;
      font-family: 'font-medium';
      font-size: var(--nn-offer-card_button-card__warning_font-size, 12px);
      order: 5;
      text-align: center;
    }
    .button-card__image {
      display: flex;
      margin: auto;
    }
    @media (max-width: 767px) {
      :host {
        --nn-offer-card_button-card__container--left_flex-basis: 100%;
        --nn-offer-card_button-card__container--right_flex-basis: 80%;
        --nn-offer-card_button-card__price_margin: 0 0 8px;
      }
      .button-card__price--promo {
        margin: 0;
      }
      .button-card__image {
        display: none;
      }
      .button-card__wrapper--right {
        padding-left: 0;
      }
    }
  `;

  @property({ type: String }) title = 'Title';

  @property({ type: String }) subtitle = null;

  @property({ type: String }) inputName = (Math.random() + 1)
    .toString(36)
    .substring(7);

  @property({ type: String }) iconUrl = '';

  @property({ type: String }) iconSize = '35px';

  @property({ type: String }) imageUrl = '';

  @property({ type: String }) imageSize = '60px';

  @property({ type: String }) price = null;

  @property({ type: String }) ttcPrice = null;

  @property({ type: Boolean }) isPro = false;

  @property({ type: String }) promoLabel = null;

  @property({ type: String }) frequency = null;

  @property({ type: Boolean }) isPromo = false;

  @property({ type: Boolean }) isFree = false;

  @property({ type: Boolean }) light = true;

  @property({ type: Boolean }) disabled = false;

  @property({ type: String }) actionName = null;

  @property({ type: Number }) actionType = 0;

  renderPrice() {
    if (this.isFree) {
      return html`
        <nn-badge
          class="button-card__price"
          text="Offert"
          variant="pink"
        ></nn-badge>
      `;
    }
    if (this.isPromo) {
      return html`
        <nn-badge
          class="button-card__price"
          text="Promo"
          variant="pink"
        ></nn-badge>
      `;
    }

    if (this.price && this.frequency) {
      return html`
        <p class="button-card__price">
          ${this.price}<span class="button-card__frequency"
            >${this.isPro ? ' HT' : ''}/${this.frequency}</span
          >
        </p>
      `;
    }
    if (this.price) {
      return html` <p class="button-card__price">${this.price}</p>`;
    }
    return '';
  }

  renderTTCPrice() {
    if (this.isPro && this.ttcPrice && this.frequency) {
      return html` <div class="button-card__container--second-price">
        soit ${this.ttcPrice} TTC/${this.frequency}
      </div>`;
    }
    return '';
  }

  renderPromoLabel() {
    if (this.promoLabel) {
      return html`
        <p class="button-card__price--promo">${this.promoLabel}</p>
      `;
    }
    return '';
  }

  renderIcon() {
    if (this.iconUrl) {
      return html`
        <img
          class="button-card__icon"
          width="${this.iconSize}"
          src="${this.iconUrl}"
          alt=""
        />
      `;
    }
    return html``;
  }

  renderImage() {
    if (this.imageUrl) {
      return html`
        <img
          class="button-card__image"
          width="${this.imageSize}"
          src="${this.imageUrl}"
          alt=""
        />
      `;
    }
    return html``;
  }

  render() {
    return html`
      <label
        class="button-card ${this.light ? 'button-card--light' : ''} ${this
          .actionType > 0
          ? `button-card--${this.actionType}`
          : ''} ${this.disabled ? ' button-card--disabled' : ''}"
        for="${this.inputName}"
      >
        <div class="button-card__wrapper">
          <div class="button-card__wrapper--right">
            <div class="button-card__container--left">
              ${this.renderIcon()}
              <div class="button-card__title_group">
                <span class="button-card__subtitle">${this.subtitle}</span>
              </div>
            </div>
            <div class="button-card__container--right">
              ${this.renderPrice()} ${this.renderPromoLabel()}
            </div>
            ${this.renderTTCPrice()}
            <div class="button-card__container--bottom">
              <slot
                name="button-card-content"
                class="button-card__content"
              ></slot>
            </div>
          </div>
        </div>
        <div class="button-card__container--action">
          <slot
            name="info"
            class="${this.disabled ? ' button-card--disabled' : ''}"
          ></slot>
        </div>
      </label>
    `;
  }
}
